// src/pages/equipes/components/CoachingStaff.tsx
import React from "react";
import { Link } from "react-router-dom";

/**
 * Imports images en chemin relatif depuis CE fichier :
 * src/pages/equipes/components/CoachingStaff.tsx
 * remonte à src/ => "../../../"
 * puis va dans assets/coaches/
 */
import imgClaire from "../../../assets/coaches/claire.jpg";
import imgJC     from "../../../assets/coaches/jc.jpg";
import imgTOTO   from "../../../assets/coaches/TOTO.jpg";
import imgSacha  from "../../../assets/coaches/sacha.jpg";
import imgFreddy from "../../../assets/coaches/freddy.jpg";

// Typage pour sécuriser la structure des données
interface Coach {
  id: number;
  name: string;
  role: string;
  experience: string; // ex. "5 ans"
  certifications: string[];
  specialties: string[];
  image: string;
  quote: string;
  focal?: string; // optionnel : "50% 35%" pour ajuster le cadrage d'une photo
}

export const CoachingStaff: React.FC = () => {
  const coaches: Coach[] = [
    {
      id: 1,
      name: "Claire",
      role: "Entraîneur – Jeunes",
      experience: "5 ans",
      certifications: ["Formation FSGT"],
      specialties: ["Tactique", "Patience"],
      image: imgClaire,
      quote: "Le volley-ball est un sport d'équipe où chaque joueur compte.",
      // focal: "50% 40%",
    },
    {
      id: 2,
      name: "Sacha",
      role: "Entraîneur – Jeunes",
      experience: "3 ans",
      certifications: ["Initiateur FSGT", "Formation Jeunes"],
      specialties: ["Pédagogie", "Développement", "Attaque de ligne"],
      image: imgSacha,
      quote: "Former les jeunes, c'est construire l'avenir du volley-ball.",
    },
    {
      id: 3,
      name: "TOTO",
      role: "Entraîneur – Équipe 4x4 Féminine",
      experience: "8 ans",
      certifications: ["Formation"],
      specialties: ["Technique", "Mental"],
      image: imgTOTO,
      quote: "La progression passe par la confiance en soi et l'esprit d'équipe.",
    },
    {
      id: 4,
      name: "JC",
      role: "Entraîneur – 4x4 Équi-mixte",
      experience: "8 ans",
      certifications: ["Formation"],
      specialties: ["Technique", "Mental"],
      image: imgJC,
      quote: "La progression passe par la confiance en soi et l'esprit d'équipe.",
    },
    {
      id: 5,
      name: "Freddy",
      role: "Entraîneur – 4x4",
      experience: "1 an",
      certifications: ["Voyageur aguerri"],
      specialties: ["Animation", "Convivialité"],
      image: imgFreddy,
      quote: "Le plaisir de jouer avant tout, dans la bonne humeur !",
    },
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Notre équipe d’encadrement
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des entraîneurs passionnés et qualifiés pour accompagner chaque joueur
            dans sa progression et son épanouissement sportif.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {coaches.map((coach) => (
            <div
              key={coach.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative">
                <img
                  src={coach.image}
                  alt={`Portrait de ${coach.name}`}
                  className="w-full aspect-[3/4] object-cover object-center"
                  style={coach.focal ? { objectPosition: coach.focal } : undefined}
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <h3 className="text-lg font-bold text-white">{coach.name}</h3>
                  <p className="text-blue-200 text-sm">{coach.role}</p>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <i className="ri-time-line mr-2 text-blue-600" />
                    <span className="text-sm text-gray-600">
                      {coach.experience} d'expérience
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Certifications :</h4>
                  <ul className="space-y-1">
                    {coach.certifications.map((cert, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <i className="ri-medal-line mr-2 text-yellow-500 w-4" />
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Spécialités :</h4>
                  <div className="flex flex-wrap gap-2">
                    {coach.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                <blockquote className="text-sm text-gray-600 italic border-l-4 border-blue-600 pl-3">
                  "{coach.quote}"
                </blockquote>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Rejoignez notre équipe d’encadrement
            </h3>
            <p className="text-gray-600 mb-6">
              Vous êtes passionné de volley-ball et souhaitez transmettre votre savoir ?
              Nous recherchons toujours de nouveaux talents pour enrichir notre équipe d’encadrement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:encadrement@csvbv.fr"
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors durée-200 whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2" />
                Nous contacter
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors durée-200 whitespace-nowrap"
              >
                <i className="ri-information-line mr-2" />
                Plus d'informations
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
